import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { Spin, Modal, message } from './antd';
import {
  FilePdf,
  FileImage,
  FileDoc,
  FileXls,
  FileZip,
  File,
  UploadSimple,
  Trash,
  DownloadSimple,
  Clock,
  Buildings,
} from '@phosphor-icons/react';
import Typography from '../../components/Typography';
import Button from '../../components/Button';
import { formatFileSize } from '../../components/portal/formatUtils';
import { formatDate } from '../../components/portal/dateUtils';
import csrfHeaders from '../../utils/csrfHeaders';
import UploadModal from './UploadModal';
import './documentsTab.scss';

const FILE_ICON_MAP = {
  '.pdf': { icon: FilePdf, color: 'red' },
  '.doc': { icon: FileDoc, color: 'blue' },
  '.docx': { icon: FileDoc, color: 'blue' },
  '.xls': { icon: FileXls, color: 'green' },
  '.xlsx': { icon: FileXls, color: 'green' },
  '.csv': { icon: FileXls, color: 'green' },
  '.png': { icon: FileImage, color: 'purple' },
  '.jpg': { icon: FileImage, color: 'purple' },
  '.jpeg': { icon: FileImage, color: 'purple' },
  '.gif': { icon: FileImage, color: 'purple' },
  '.bmp': { icon: FileImage, color: 'purple' },
  '.tiff': { icon: FileImage, color: 'purple' },
  '.tif': { icon: FileImage, color: 'purple' },
  '.zip': { icon: FileZip, color: 'orange' },
  '.rar': { icon: FileZip, color: 'orange' },
  '.7z': { icon: FileZip, color: 'orange' },
};

const getFileIcon = (extension) => {
  const config = FILE_ICON_MAP[extension?.toLowerCase()];
  if (config) return config;
  return { icon: File, color: 'gray' };
};

const DocumentsTab = ({
  referralEntitySlug, archived, currentOrganizationId, lastNotificationAt,
}) => {
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [deletingSlug, setDeletingSlug] = useState(null);

  const fetchAttachments = useCallback(async () => {
    if (!referralEntitySlug) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        `/api/v1/referral_entities/${referralEntitySlug}/attachments`,
      );
      setAttachments(response.data.attachments || []);
    } catch (err) {
      console.error('Failed to load documents:', err);
      setError('Failed to load documents. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [referralEntitySlug]);

  useEffect(() => {
    fetchAttachments();
  }, [fetchAttachments]);

  useEffect(() => {
    if (lastNotificationAt) {
      fetchAttachments();
    }
  }, [lastNotificationAt]);

  const handleRowClick = async (attachment) => {
    try {
      const response = await axios.get(
        `/api/v1/referral_entities/${referralEntitySlug}/attachments/${attachment.slug}`,
      );
      const { download_url: downloadUrl } = response.data;
      if (downloadUrl) {
        window.open(downloadUrl, '_blank', 'noopener,noreferrer');
      }
    } catch (err) {
      console.error('Failed to get download URL:', err);
      message.error('Failed to open document. Please try again.');
    }
  };

  const handleDeleteConfirm = async (attachment) => {
    setDeletingSlug(attachment.slug);
    try {
      await axios.delete(
        `/api/v1/referral_entities/${referralEntitySlug}/attachments/${attachment.slug}`,
        { headers: csrfHeaders() },
      );
      message.success('Document deleted successfully');
      fetchAttachments();
    } catch (err) {
      console.error('Failed to delete document:', err);
      message.error('Failed to delete document. Please try again.');
    } finally {
      setDeletingSlug(null);
    }
  };

  const handleDeleteClick = (e, attachment) => {
    e.stopPropagation();
    Modal.confirm({
      title: 'Delete Document',
      content: `Are you sure you want to delete "${attachment.original_filename}"? This action cannot be undone.`,
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: () => handleDeleteConfirm(attachment),
    });
  };

  const handleUploadComplete = () => {
    fetchAttachments();
  };

  const canDelete = (attachment) => {
    if (!currentOrganizationId || !attachment.uploaded_by) return false;
    return attachment.uploaded_by.organization_id === currentOrganizationId;
  };

  if (loading) {
    return (
      <div className="documents-tab">
        <div className="documents-tab__loading">
          <Spin size="large" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="documents-tab">
        <div className="documents-tab__error">
          <Typography variant="text" size="small">
            {error}
          </Typography>
          <div className="documents-tab__retry-btn">
            <Button
              type="secondary"
              size="small"
              label="Retry"
              onClick={fetchAttachments}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="documents-tab">
      <div className="documents-tab__header">
        <div className="documents-tab__header-left">
          <Typography variant="text" size="medium" bold>
            Shared Documents
          </Typography>
          <Typography variant="text" size="small" className="documents-tab__subtitle">
            Documents shared between organizations for this referral
          </Typography>
        </div>
        {!archived && (
          <div className="documents-tab__upload-btn">
            <Button
              type="primary"
              size="small"
              label="Upload Document"
              leftIcon={<UploadSimple size={16} weight="bold" />}
              onClick={() => setUploadModalVisible(true)}
            />
          </div>
        )}
      </div>

      {attachments.length === 0 ? (
        <div className="documents-tab__empty">
          <File size={48} weight="thin" />
          <Typography variant="text" size="small" className="documents-tab__empty-text">
            No documents shared yet
          </Typography>
          <Typography variant="text" size="xsmall" className="documents-tab__empty-hint">
            {archived
              ? 'This referral is archived. Document uploads are disabled.'
              : 'Upload documents to share them with the other organization.'}
          </Typography>
        </div>
      ) : (
        <div className="documents-tab__list">
          {attachments.map((attachment) => {
            const fileConfig = getFileIcon(attachment.file_extension);
            const IconComponent = fileConfig.icon;
            const isDeleting = deletingSlug === attachment.slug;

            return (
              <div
                key={attachment.slug || attachment.id}
                className={`documents-tab__row ${isDeleting ? 'documents-tab__row--deleting' : ''}`}
                onClick={() => handleRowClick(attachment)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleRowClick(attachment);
                }}
              >
                <div className={`documents-tab__file-icon documents-tab__file-icon--${fileConfig.color}`}>
                  <IconComponent size={24} weight="duotone" />
                </div>

                <div className="documents-tab__file-info">
                  <Typography variant="text" size="small" bold className="documents-tab__filename">
                    {attachment.original_filename}
                  </Typography>
                  <div className="documents-tab__file-meta">
                    <span className="documents-tab__meta-item">
                      {formatFileSize(attachment.file_size)}
                    </span>
                    <span className="documents-tab__meta-separator" />
                    <span className="documents-tab__meta-item">
                      <Buildings size={12} />
                      {attachment.uploaded_by?.organization_name || 'Unknown'}
                    </span>
                    <span className="documents-tab__meta-separator" />
                    <span className="documents-tab__meta-item">
                      <Clock size={12} />
                      {formatDate(attachment.created_at)}
                    </span>
                  </div>
                </div>

                <div className="documents-tab__row-actions">
                  <button
                    type="button"
                    className="documents-tab__action-btn documents-tab__action-btn--download"
                    title="Download"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRowClick(attachment);
                    }}
                  >
                    <DownloadSimple size={16} />
                  </button>

                  {canDelete(attachment) && (
                    <button
                      type="button"
                      className="documents-tab__action-btn documents-tab__action-btn--delete"
                      title="Delete"
                      disabled={isDeleting}
                      onClick={(e) => handleDeleteClick(e, attachment)}
                    >
                      {isDeleting ? <Spin size="small" /> : <Trash size={16} />}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!archived && (
        <UploadModal
          visible={uploadModalVisible}
          onClose={() => setUploadModalVisible(false)}
          referralEntitySlug={referralEntitySlug}
          onUploadComplete={handleUploadComplete}
        />
      )}
    </div>
  );
};

DocumentsTab.propTypes = {
  referralEntitySlug: PropTypes.string,
  archived: PropTypes.bool,
  currentOrganizationId: PropTypes.number,
  lastNotificationAt: PropTypes.number,
};

DocumentsTab.defaultProps = {
  referralEntitySlug: null,
  archived: false,
  currentOrganizationId: null,
  lastNotificationAt: null,
};

export default DocumentsTab;
